import React from 'react';
import { useHistory } from 'react-router-dom';
import IconButton from '@applied/dragonfruit/src/components/IconButton/IconButton';
import Flex from '@applied/dragonfruit/src/components/Layout/Flex';
import LoadingSkeleton from '@applied/dragonfruit/src/components/LoadingSkeleton/LoadingSkeleton';
import OverflowTextTooltip from '@applied/dragonfruit/src/components/Text/OverflowTextTooltip';
import { Typography } from '@applied/dragonfruit/src/components/Text/Typography';
import { useQuery } from '@tanstack/react-query';

import { ImageWithBackups } from 'components/common/ImageWithBackups';
import { getIcon, WebOfferIcon } from 'components/WebOfferPage/icon_requests';
import type { EmployeeFields } from 'components/WebOfferPage/web_offer_context';

import PlaceholderHeadshot from 'assets/placeholder_headshot.svg';

type TeamMemberSummaryProps = {
  employee: EmployeeFields;
};

export function TeamMemberSummary(props: TeamMemberSummaryProps): JSX.Element {
  const {
    employee: { profileImageUrl, firstName, lastName, email, linkedinUrl },
  } = props;
  const history = useHistory();
  const loadingSkeleton = <LoadingSkeleton width="100px" height="100px" />;

  const { data: emailIconResponse } = useQuery({
    queryKey: ['icon', WebOfferIcon.EMAIL],
    queryFn: () => getIcon(WebOfferIcon.EMAIL, history),
  });

  const { data: linkedinIconResponse } = useQuery({
    queryKey: ['icon', WebOfferIcon.LINKEDIN],
    queryFn: () => getIcon(WebOfferIcon.LINKEDIN, history),
  });

  return (
    <Flex
      className="team-member-summary"
      direction="column"
      gap="sm"
      alignment="center"
      justify="center"
    >
      {firstName && lastName && email ? (
        <>
          <ImageWithBackups
            className="profile-image"
            imageSources={profileImageUrl ? [profileImageUrl] : []}
            defaultImage={PlaceholderHeadshot}
          />
          <Typography className="team-member-name" variant="caption">
            <OverflowTextTooltip text={`${firstName} ${lastName}`} />
          </Typography>
          <Flex className="team-member-icons" direction="row" gap="sm">
            <a href={`mailto:${email}`}>
              {emailIconResponse?.iconUrl ? (
                <Flex
                  className="team-member-image-container"
                  direction="row"
                  justify="center"
                  alignment="center"
                >
                  <img className="team-member-image" src={emailIconResponse.iconUrl} />
                </Flex>
              ) : (
                <IconButton
                  className="team-member-icon"
                  icon="ARCHIVE"
                  backgroundColor="#fff"
                  enabledColor="#06c"
                />
              )}
            </a>
            {linkedinUrl && (
              <a href={linkedinUrl} target="_blank" rel="noreferrer">
                {linkedinIconResponse?.iconUrl ? (
                  <Flex
                    className="team-member-image-container"
                    direction="row"
                    justify="center"
                    alignment="center"
                  >
                    <img className="team-member-image" src={linkedinIconResponse.iconUrl} />
                  </Flex>
                ) : (
                  <IconButton
                    className="team-member-icon"
                    icon="LINK"
                    backgroundColor="#fff"
                    enabledColor="#06c"
                  />
                )}
              </a>
            )}
          </Flex>
        </>
      ) : (
        loadingSkeleton
      )}
    </Flex>
  );
}
